import { FormEvent, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { ActiveStatusBadge } from '../../components/ui/ActiveStatusBadge';
import { FormActionBar } from '../../components/ui/FormActionBar';
import { CollapsibleFormSection } from '../../components/ui/CollapsibleFormSection';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import { EditIconButton } from '../../components/ui/EditIconButton';
import { ListPaginationBar } from '../../components/ui/ListPaginationBar';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { RequiredMark } from '../../components/ui/RequiredMark';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import { useCollapsibleItemForm } from '../../hooks/useCollapsibleItemForm';
import { getRequestErrorMessage } from '../../utils/getRequestErrorMessage';
import { formatCnpjMask, isValidCnpj, normalizeCnpj } from '../../utils/cnpjValidator';
import {
  Company,
  CompanyUpsertPayload,
  createCompany,
  deleteCompany,
  listCompanies,
  updateCompany,
} from '../../services/coreCompaniesApi';

const PAGE_SIZE = 10;

const emptyForm: CompanyUpsertPayload = {
  name: '',
  legalName: '',
  document: '',
  email: '',
  phone: '',
  website: '',
  notes: '',
  isActive: true,
};

export function CompaniesPage() {
  const { canWriteModule } = usePermissions();
  const { confirm } = useConfirmDialog();
  const canWrite = canWriteModule('core.companies');
  const { formExpanded, expandForm, collapseForm, handleCreateSuccess, toggleOrCancel } =
    useCollapsibleItemForm();
  const [items, setItems] = useState<Company[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<CompanyUpsertPayload>(emptyForm);
  const [saving, setSaving] = useState(false);
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  async function loadData(targetPage = page): Promise<void> {
    setLoading(true);
    try {
      const result = await listCompanies({ page: targetPage, pageSize: PAGE_SIZE, search: search || undefined });
      setItems(result.items);
      setTotalCount(result.totalCount);
      setPage(result.page);
    } catch {
      toast.error('Falha ao carregar empresas.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  function resetForm(): void {
    setEditingId(null);
    setForm(emptyForm);
  }

  function startEdit(company: Company): void {
    expandForm();
    setEditingId(company.id);
    setForm({
      name: company.name,
      legalName: company.legalName ?? '',
      document: formatCnpjMask(company.document ?? ''),
      email: company.email ?? '',
      phone: company.phone ?? '',
      website: company.website ?? '',
      notes: company.notes ?? '',
      isActive: company.isActive,
    });
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite) return;

    const document = (form.document ?? '').trim();
    if (document && !isValidCnpj(document)) {
      toast.error('CNPJ inválido. Informe um CNPJ numérico ou alfanumérico válido.');
      return;
    }

    setSaving(true);
    try {
      const payload: CompanyUpsertPayload = {
        ...form,
        document: document ? normalizeCnpj(document) : undefined,
      };
      if (editingId) {
        await updateCompany(editingId, payload);
        toast.success('Empresa atualizada.');
        collapseForm(resetForm);
      } else {
        await createCompany(payload);
        toast.success('Empresa criada.');
        handleCreateSuccess(resetForm);
      }
      await loadData(page);
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao salvar empresa.'));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(company: Company): Promise<void> {
    if (
      !canWrite
      || !(await confirm({
        title: 'Excluir empresa',
        message: `Excluir "${company.name}"?`,
        confirmLabel: 'Excluir',
        tone: 'danger',
      }))
    ) {
      return;
    }

    try {
      await deleteCompany(company.id);
      toast.success('Empresa excluída.');
      await loadData(page);
    } catch {
      toast.error('Falha ao excluir empresa.');
    }
  }

  return (
    <PageShell>
      <PageHeader
        title="Clientes"
        description="Cadastro de clientes (organizações) atendidas pela empresa."
      />

      {canWrite ? (
        <CollapsibleFormSection
          title={editingId ? 'Editar empresa' : 'Nova empresa'}
          expanded={formExpanded}
          onToggle={() => toggleOrCancel(resetForm)}
          editingSummary={editingId ? `Editando ${form.name || 'empresa'} — o formulário abriu em uma janela` : undefined}
          createSummary="Clique para cadastrar uma nova empresa"
          isEditing={!!editingId}
        >
        <form onSubmit={(event) => void handleSubmit(event)} className="erp-form px-4 pb-4 pt-3">
          <div className="erp-form-grid">
              <label className="erp-label">
                Nome <RequiredMark />
                <input
                  className="erp-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </label>
              <label className="erp-label">
                CNPJ
                <input
                  className="erp-input font-mono"
                  value={form.document ?? ''}
                  onChange={(e) => setForm({ ...form, document: formatCnpjMask(e.target.value) })}
                  placeholder="00.000.000/0000-00"
                  autoComplete="off"
                  spellCheck={false}
                  maxLength={18}
                />
              </label>
              <label className="erp-label">
                E-mail
                <input
                  type="email"
                  className="erp-input"
                  value={form.email ?? ''}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </label>
            </div>
            <FormActionBar
              onCancel={() => collapseForm(resetForm)}
              cancelLabel="Cancelar"
              className="erp-btn-actions mt-4"
            >
              <button type="submit" disabled={saving} className="erp-btn-primary">
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </FormActionBar>
        </form>
        </CollapsibleFormSection>
      ) : null}

      <div className="mb-4 flex gap-2">
        <input
          className="erp-input max-w-md"
          placeholder="Buscar por nome ou CNPJ..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button type="button" className="erp-btn-secondary" onClick={() => void loadData(1)}>
          Buscar
        </button>
      </div>

      {loading ? (
        <LoadingSpinner label="Carregando empresas..." />
      ) : (
        <div className="erp-card overflow-x-auto">
          <table className="erp-table w-full">
            <thead>
              <tr>
                <th>Nome</th>
                <th>CNPJ</th>
                <th>E-mail</th>
                <th>Status</th>
                {canWrite ? <th /> : null}
              </tr>
            </thead>
            <tbody>
              {items.map((company) => (
                <tr key={company.id}>
                  <td>{company.name}</td>
                  <td>{company.document ? formatCnpjMask(company.document) : '—'}</td>
                  <td>{company.email ?? '—'}</td>
                  <td>
                    <ActiveStatusBadge active={company.isActive} feminine />
                  </td>
                  {canWrite ? (
                    <td className="text-right">
                      <EditIconButton onClick={() => startEdit(company)} />
                      <DeleteIconButton onClick={() => void handleDelete(company)} />
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ListPaginationBar
        totalCount={totalCount}
        page={page}
        pageSize={PAGE_SIZE}
        onPrevious={() => setPage((current) => Math.max(1, current - 1))}
        onNext={() => setPage((current) => Math.min(totalPages, current + 1))}
      />
    </PageShell>
  );
}
